"use client";

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState } from "react";

interface OrdersBackProps {
    trigger: React.ReactNode;
}

export function OrdersBack({ trigger }: OrdersBackProps) {
    const pathname = usePathname();
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    // Only show on orders routes
    const isOrdersRoute = pathname?.startsWith("/orders/");

    if (!isOrdersRoute) {
        return null;
    }

    const handleTriggerClick = () => {
        setShowConfirmDialog(true);
    };

    const handleCancel = () => {
        setShowConfirmDialog(false);
    };

    return (
        <>
            <div onClick={handleTriggerClick}>{trigger}</div>

            <AlertDialog
                open={showConfirmDialog}
                onOpenChange={setShowConfirmDialog}
            >
                <AlertDialogContent className="sm:max-w-md overflow-hidden rounded-3xl bg-gradient-to-br from-white via-blue-50 to-indigo-50 dark:from-gray-800 dark:via-blue-950 dark:to-indigo-950 shadow-xl border border-white/20 dark:border-gray-700/30 backdrop-blur-sm">
                    {/* Decorative Elements */}
                    <div className="absolute top-0 right-0 -mt-4 -mr-4 h-24 w-24 rounded-full bg-gradient-to-br from-purple-400/20 to-violet-400/20 blur-2xl pointer-events-none -z-10" />
                    <div className="absolute bottom-0 left-0 -mb-4 -ml-4 h-32 w-32 rounded-full bg-gradient-to-tr from-violet-400/20 to-purple-400/20 blur-2xl pointer-events-none -z-10" />

                    {/* Content */}
                    <div className="relative w-full">
                        <AlertDialogHeader className="pb-4">
                            <AlertDialogTitle className="text-xl font-bold text-gray-900 dark:text-white">
                                Exit Assessment
                            </AlertDialogTitle>
                        </AlertDialogHeader>

                        <div className="space-y-4">
                            <AlertDialogDescription className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                                Are you sure you want to exit this assessment?
                                Your progress will be saved and you can continue
                                later.
                            </AlertDialogDescription>
                        </div>

                        <AlertDialogFooter className="pt-4">
                            <div className="flex justify-between w-full gap-3">
                                <AlertDialogCancel
                                    onClick={handleCancel}
                                    className="h-10 bg-white/50 dark:bg-gray-800/50 hover:bg-white/80 dark:hover:bg-gray-800/80 border-gray-200 dark:border-gray-600 transition-all duration-300"
                                >
                                    Cancel
                                </AlertDialogCancel>
                                <Link href="/dashboard">
                                    <AlertDialogAction className="h-10 bg-gradient-to-r from-blue-600 to-indigo-600 text-white transition-all duration-200 hover:shadow-lg transform hover:-translate-y-0.5">
                                        Exit
                                    </AlertDialogAction>
                                </Link>
                            </div>
                        </AlertDialogFooter>
                    </div>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
